import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { 
  Shield, 
  FileText, 
  Sparkles, 
  BarChart3, 
  Scale, 
  Cpu,
  ArrowRight,
  CheckCircle2,
  Newspaper,
  TrendingUp,
  Users,
  PenTool,
  Brain,
  Calendar,
  Star,
  Zap,
  Settings
} from 'lucide-react';
import AdminLoginModal from '@/components/AdminLoginModal';

const LandingPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const [showAdminModal, setShowAdminModal] = useState(false);

  const previewTabs = [
    { label: 'Dashboard', icon: <BarChart3 className="w-3.5 h-3.5" /> },
    { label: 'Análisis IA', icon: <Brain className="w-3.5 h-3.5" /> },
    { label: 'Firmas', icon: <PenTool className="w-3.5 h-3.5" /> },
    { label: 'Calendario', icon: <Calendar className="w-3.5 h-3.5" /> },
  ];

  useEffect(() => {
    if (!autoPlay) return;
    const interval = setInterval(() => {
      setActiveTab(prev => (prev + 1) % previewTabs.length);
    }, 3200);
    return () => clearInterval(interval);
  }, [autoPlay, previewTabs.length]);

  const handleTabClick = (idx) => {
    setActiveTab(idx);
    setAutoPlay(false);
  };
  const features = [
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "Generación con IA",
      description: "Crea contratos legales personalizados mediante una entrevista inteligente guiada por IA."
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Análisis de Riesgo",
      description: "Obtén un puntaje de conflicto y análisis detallado de cláusulas problemáticas."
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Traductor Legal",
      description: "Convierte el lenguaje jurídico complejo en explicaciones simples y claras."
    },
    {
      icon: <Scale className="w-6 h-6" />,
      title: "Detección de Sesgo",
      description: "Identifica cláusulas desequilibradas o potencialmente abusivas."
    },
    {
      icon: <Cpu className="w-6 h-6" />,
      title: "Smart Contracts",
      description: "Genera versiones ejecutables en blockchain de tus contratos legales."
    },
    {
      icon: <Newspaper className="w-6 h-6" />,
      title: "Actualidad Jurídica",
      description: "Mantente actualizado con cambios legales en tiempo real."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Firmas Múltiples",
      description: "Invita a varios firmantes con flujo secuencial o paralelo y seguimiento en tiempo real."
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: "Rastreador de Vencimientos",
      description: "Registra fechas clave y recibe alertas antes de que venzan tus contratos."
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Importar Contratos",
      description: "Sube tus contratos existentes en PDF o Word para analizarlos y gestionarlos."
    }
  ];

  const benefits = [
    "Ahorra hasta un 90% del tiempo en redacción de contratos",
    "Reduce riesgos legales con análisis predictivo",
    "Firma digital integrada y exportación a PDF",
    "Plantillas prediseñadas para todo tipo de contratos",
    "Interfaz completamente en español"
  ];

  return (
    <div className="min-h-screen mesh-gradient">
      {/* Header */}
      <header className="glass fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <Shield className="w-8 h-8 text-blue-500" />
              <span className="text-xl font-bold font-['Outfit']">NotaryOS</span>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowAdminModal(true)}
                className="flex items-center space-x-1.5 px-3 py-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors text-sm"
                data-testid="admin-access-btn"
              >
                <Settings className="w-4 h-4" />
                <span className="hidden sm:inline">Admin</span>
              </button>
              <Link 
                to="/auth"
                data-testid="header-login-btn"
                className="btn-primary px-5 py-2 text-sm"
              >
                Iniciar Sesión
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Admin Login Modal */}
      <AdminLoginModal 
        isOpen={showAdminModal} 
        onClose={() => setShowAdminModal(false)} 
      />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-30"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-blue-500/10 border border-blue-500/20 rounded-full px-4 py-1.5 mb-8 animate-fade-in">
              <Sparkles className="w-4 h-4 text-blue-400" />
              <span className="text-sm text-blue-400">Potenciado por Inteligencia Artificial</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold font-['Outfit'] tracking-tight mb-6 animate-slide-up">
              El Guardián Legal
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                Autónomo
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-400 mb-10 max-w-2xl mx-auto animate-fade-in stagger-2">
              Elimina la fricción burocrática y asegura tu protección legal con análisis 
              predictivo impulsado por IA. Crea, analiza y firma contratos en minutos.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in stagger-3">
              <Link 
                to="/auth"
                data-testid="hero-start-btn"
                className="btn-primary px-8 py-3 text-lg flex items-center space-x-2 animate-pulse-glow"
              >
                <span>Comenzar Gratis</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link 
                to="/auth"
                data-testid="hero-demo-btn"
                className="px-8 py-3 text-lg border border-slate-600 rounded-md hover:bg-slate-800 hover:border-slate-500 transition-all duration-200"
              >
                Ver Demo
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold font-['Outfit'] mb-4">
              Todo lo que necesitas para tus documentos legales
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Una plataforma completa que combina la precisión legal con el poder de la inteligencia artificial.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-card border border-border rounded-lg p-6 card-hover animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center text-blue-400 mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold font-['Outfit'] mb-2">{feature.title}</h3>
                <p className="text-slate-400 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-900/5 to-transparent"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-semibold font-['Outfit'] mb-6">
                ¿Por qué elegir NotaryOS?
              </h2>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start space-x-3 animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-300">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="bg-card border border-border rounded-xl shadow-2xl overflow-hidden">
                {/* Window Bar */}
                <div className="flex items-center justify-between px-4 py-3 bg-slate-800/80 border-b border-border">
                  <div className="flex items-center space-x-1.5">
                    <div className="w-3 h-3 rounded-full bg-rose-500"></div>
                    <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                    <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                  </div>
                  {/* Tab Selector */}
                  <div className="flex items-center gap-1">
                    {previewTabs.map((tab, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleTabClick(idx)}
                        className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                          activeTab === idx
                            ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                            : 'text-slate-500 hover:text-slate-300'
                        }`}
                      >
                        {tab.icon}
                        <span className="hidden sm:inline">{tab.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Preview Content */}
                <div className="p-5 min-h-[280px]">
                  {/* Tab 0: Dashboard */}
                  {activeTab === 0 && (
                    <div className="space-y-3 animate-fade-in">
                      <p className="text-xs text-slate-400 font-medium mb-3">📊 Resumen Legal — Enero 2026</p>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { label: 'Contratos', value: '12', color: 'text-blue-400', bg: 'bg-blue-500/10' },
                          { label: 'Firmados', value: '9', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
                          { label: 'Riesgo Prom.', value: '28', color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
                        ].map((s, i) => (
                          <div key={i} className={`${s.bg} rounded-lg p-3 text-center`}>
                            <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
                            <p className="text-xs text-slate-400 mt-0.5">{s.label}</p>
                          </div>
                        ))}
                      </div>
                      <div className="space-y-2 mt-2">
                        {[
                          { title: 'Contrato Arriendo Oficina', status: 'Firmado', color: 'text-emerald-400 bg-emerald-500/10' },
                          { title: 'Acuerdo de Confidencialidad', status: 'Borrador', color: 'text-yellow-400 bg-yellow-500/10' },
                          { title: 'Contrato de Servicios TI', status: 'En revisión', color: 'text-blue-400 bg-blue-500/10' },
                        ].map((c, i) => (
                          <div key={i} className="flex items-center justify-between bg-slate-800/50 rounded-lg px-3 py-2">
                            <div className="flex items-center gap-2">
                              <FileText className="w-3.5 h-3.5 text-slate-500" />
                              <span className="text-xs text-slate-300">{c.title}</span>
                            </div>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${c.color}`}>{c.status}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Tab 1: AI Analysis */}
                  {activeTab === 1 && (
                    <div className="space-y-3 animate-fade-in">
                      <p className="text-xs text-slate-400 font-medium mb-3">🤖 Análisis de Riesgo con IA</p>
                      <div className="bg-slate-800/50 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium">Puntaje de Riesgo</span>
                          <div className="flex items-center gap-2">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center text-black font-bold text-sm">42</div>
                            <span className="text-xs text-yellow-400">Moderado</span>
                          </div>
                        </div>
                        <div className="space-y-2">
                          {[
                            { label: 'Cláusula de arbitraje unilateral', risk: 'Alto', color: 'bg-red-500' },
                            { label: 'Sin fecha de vencimiento', risk: 'Medio', color: 'bg-yellow-500' },
                            { label: 'Penalidades desequilibradas', risk: 'Medio', color: 'bg-yellow-500' },
                          ].map((issue, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <div className={`w-1.5 h-1.5 rounded-full ${issue.color} flex-shrink-0`}></div>
                              <span className="text-xs text-slate-300 flex-1">{issue.label}</span>
                              <span className={`text-xs px-1.5 py-0.5 rounded text-white ${issue.color}`}>{issue.risk}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
                        <p className="text-xs text-emerald-400 flex items-start gap-2">
                          <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                          <span>IA detectó 3 cláusulas protectoras recomendadas para este tipo de contrato</span>
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Tab 2: Signatures */}
                  {activeTab === 2 && (
                    <div className="space-y-3 animate-fade-in">
                      <p className="text-xs text-slate-400 font-medium mb-3">✍️ Flujo de Firmas Múltiples</p>
                      <div className="space-y-2">
                        {[
                          { name: 'Juan Pérez (Vendedor)', status: 'signed', time: 'hace 2h' },
                          { name: 'María González (Compradora)', status: 'signed', time: 'hace 1h' },
                          { name: 'Carlos Notario (Testigo)', status: 'pending', time: 'Pendiente' },
                        ].map((signer, i) => (
                          <div key={i} className="flex items-center gap-3 bg-slate-800/50 rounded-lg p-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                              signer.status === 'signed' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-400'
                            }`}>
                              {signer.name[0]}
                            </div>
                            <div className="flex-1">
                              <p className="text-xs font-medium text-slate-200">{signer.name}</p>
                              <p className="text-xs text-slate-500">{signer.time}</p>
                            </div>
                            {signer.status === 'signed' 
                              ? <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                              : <div className="w-4 h-4 rounded-full border-2 border-dashed border-slate-600" />
                            }
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center justify-between bg-blue-500/10 rounded-lg p-2.5">
                        <span className="text-xs text-blue-300">Progreso: 2/3 firmas</span>
                        <div className="w-24 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                          <div className="w-2/3 h-full bg-blue-500 rounded-full"></div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Tab 3: Calendar */}
                  {activeTab === 3 && (
                    <div className="space-y-3 animate-fade-in">
                      <p className="text-xs text-slate-400 font-medium mb-3">📅 Fechas y Vencimientos</p>
                      <div className="space-y-2">
                        {[
                          { label: 'Vencimiento Arriendo', days: 8, type: 'urgent' },
                          { label: 'Renovación Seguro', days: 15, type: 'warning' },
                          { label: 'Pago Cuota Nro 3', days: 3, type: 'overdue' },
                          { label: 'Revisión Anual Contrato', days: 45, type: 'ok' },
                        ].map((item, i) => {
                          const colors = {
                            urgent: 'border-l-yellow-500 text-yellow-400',
                            warning: 'border-l-orange-500 text-orange-400',
                            overdue: 'border-l-red-500 text-red-400',
                            ok: 'border-l-blue-500 text-blue-400'
                          };
                          return (
                            <div key={i} className={`flex items-center justify-between border-l-2 ${colors[item.type].split(' ')[0]} bg-slate-800/50 rounded-r-lg pl-3 pr-4 py-2`}>
                              <div className="flex items-center gap-2">
                                <Calendar className="w-3.5 h-3.5 text-slate-500" />
                                <span className="text-xs text-slate-200">{item.label}</span>
                              </div>
                              <span className={`text-xs font-medium ${colors[item.type].split(' ')[1]}`}>
                                {item.type === 'overdue' ? `¡${item.days}d!` : `${item.days}d`}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-400 mt-2">
                        <Zap className="w-3 h-3 text-yellow-400" />
                        <span>NotaryOS te alertará antes de cada vencimiento</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Progress dots */}
                <div className="flex justify-center gap-1.5 pb-4">
                  {previewTabs.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleTabClick(idx)}
                      className={`h-1.5 rounded-full transition-all ${
                        activeTab === idx ? 'w-6 bg-blue-500' : 'w-1.5 bg-slate-700'
                      }`}
                    />
                  ))}
                </div>
              </div>
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-blue-500/20 rounded-full blur-2xl"></div>
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-cyan-500/20 rounded-full blur-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 px-4 border-y border-border">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { value: '198+', label: 'Plantillas Legales', icon: <FileText className="w-6 h-6 text-blue-400 mx-auto mb-2" /> },
              { value: '11', label: 'Áreas del Derecho', icon: <Scale className="w-6 h-6 text-purple-400 mx-auto mb-2" /> },
              { value: '99%', label: 'Precisión IA', icon: <Brain className="w-6 h-6 text-emerald-400 mx-auto mb-2" /> },
              { value: '24/7', label: 'Asistente Legal', icon: <Sparkles className="w-6 h-6 text-yellow-400 mx-auto mb-2" /> },
            ].map((stat, i) => (
              <div key={i} className="p-4">
                {stat.icon}
                <p className="text-2xl md:text-3xl font-bold font-['Outfit'] text-white">{stat.value}</p>
                <p className="text-sm text-slate-400 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold font-['Outfit'] mb-4">
              Planes simples y transparentes
            </h2>
            <p className="text-slate-400">Comienza gratis, actualiza cuando lo necesites</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Free Plan */}
            <div className="bg-card border border-border rounded-xl p-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold font-['Outfit']">Gratuito</h3>
                <div className="mt-2">
                  <span className="text-3xl font-bold">$0</span>
                  <span className="text-slate-400 ml-1">/mes</span>
                </div>
              </div>
              <ul className="space-y-3 mb-6">
                {['5 análisis de riesgo/mes', '3 contratos activos', 'Biblioteca de 198 plantillas', 'Exportación PDF/Word', 'Chat IA (10 msg/día)'].map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <Link to="/auth" className="block text-center px-4 py-2.5 border border-border rounded-lg hover:bg-slate-800 transition-colors text-sm font-medium">
                Comenzar Gratis
              </Link>
            </div>
            {/* Premium Plan */}
            <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 border border-blue-500/30 rounded-xl p-6 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <span className="flex items-center gap-1 bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                  <Star className="w-3 h-3" /> Popular
                </span>
              </div>
              <div className="mb-4">
                <h3 className="text-lg font-semibold font-['Outfit'] text-blue-300">Premium</h3>
                <div className="mt-2">
                  <span className="text-3xl font-bold">$9.990</span>
                  <span className="text-slate-400 ml-1">CLP/mes</span>
                </div>
                <p className="text-xs text-blue-400 mt-1">o $95.904 CLP/año (20% descuento)</p>
              </div>
              <ul className="space-y-3 mb-6">
                {['Todo ilimitado', 'Firmas biométricas', 'Colaboración en tiempo real', 'Rastreador de vencimientos', 'Asesor de cláusulas protectoras', 'Historial de versiones'].map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-slate-200">
                    <CheckCircle2 className="w-4 h-4 text-blue-400 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <Link to="/auth" className="block text-center btn-primary px-4 py-2.5 text-sm font-medium">
                Comenzar Premium
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-semibold font-['Outfit'] mb-6">
            Comienza a proteger tus intereses legales hoy
          </h2>
          <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
            Únete a miles de profesionales que ya confían en NotaryOS para sus documentos legales.
          </p>
          <Link 
            to="/auth"
            data-testid="cta-start-btn"
            className="btn-primary px-10 py-4 text-lg inline-flex items-center space-x-2"
          >
            <span>Crear Cuenta Gratuita</span>
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Brand */}
            <div className="md:col-span-1">
              <div className="flex items-center space-x-3 mb-4">
                <Shield className="w-6 h-6 text-blue-500" />
                <span className="font-semibold font-['Outfit']">NotaryOS</span>
              </div>
              <p className="text-sm text-slate-400">
                El Guardián Legal Autónomo. Plataforma de gestión de contratos con IA.
              </p>
            </div>
            
            {/* Legal Links */}
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/legal/privacidad" className="text-slate-400 hover:text-white transition-colors">
                    Política de Privacidad
                  </Link>
                </li>
                <li>
                  <Link to="/legal/terminos" className="text-slate-400 hover:text-white transition-colors">
                    Términos y Condiciones
                  </Link>
                </li>
                <li>
                  <Link to="/legal/cookies" className="text-slate-400 hover:text-white transition-colors">
                    Política de Cookies
                  </Link>
                </li>
                <li>
                  <Link to="/legal/aviso-legal" className="text-slate-400 hover:text-white transition-colors">
                    Aviso Legal
                  </Link>
                </li>
              </ul>
            </div>
            
            {/* Product */}
            <div>
              <h4 className="font-semibold text-white mb-4">Producto</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/auth" className="text-slate-400 hover:text-white transition-colors">
                    Crear Cuenta
                  </Link>
                </li>
                <li>
                  <Link to="/verificar" className="text-slate-400 hover:text-white transition-colors">
                    Verificar Documento
                  </Link>
                </li>
                <li>
                  <Link to="/support" className="text-slate-400 hover:text-white transition-colors">
                    Centro de Ayuda
                  </Link>
                </li>
              </ul>
            </div>
            
            {/* Company */}
            <div>
              <h4 className="font-semibold text-white mb-4">Empresa</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><strong>NotaryOS SpA</strong></li>
                <li>Santiago, Chile</li>
                <li>legal@notaryos.cl</li>
              </ul>
            </div>
          </div>
          
          {/* Bottom Bar */}
          <div className="pt-8 border-t border-border flex flex-col md:flex-row items-center justify-between text-sm text-slate-500">
            <p>© 2026 NotaryOS SpA. Todos los derechos reservados.</p>
            <div className="flex items-center space-x-4 mt-4 md:mt-0">
              <button 
                onClick={() => {
                  localStorage.removeItem('notaryos_cookie_consent');
                  window.location.reload();
                }}
                className="hover:text-white transition-colors"
              >
                Configurar Cookies
              </button>
              <span className="text-slate-700">|</span>
              <span className="text-slate-400">Ley 19.628 • Ley 19.496</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
